import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { forkJoin, Subject, defer } from 'rxjs';
import { ServiceordreService } from 'src/app/api/services';
import { takeUntil, finalize, tap } from 'rxjs/operators';
import { LoaderService } from 'src/app/services/loader.service';
import { GetServiceordrerRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/get-serviceordrer-request';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { SignalrService } from 'src/app/services/signalr.service';
import { ServiceordreDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-dto';
import { OrdreService } from 'src/app/services/ordre.service';
import { ServiceordreValidationFailedReasonEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceordre-validation-failed-reason-enum';
import { ServiceordreStatusEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceordre-status-enum';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';

@Component({
    selector: 'app-ordre',
    templateUrl: './ordre.component.html',
    styleUrls: ['./ordre.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OrdreComponent implements OnInit {
  serviceordreStatusEnum = ServiceordreStatusEnum;
  activePage: number = 1;
  activePageCount: number;

  pastPage: number = 1;
  pastPageCount: number;

  activeServiceordrer: ServiceordreDto[] = [];
  pastServiceordrer: ServiceordreDto[] = [];

  activeColumns: string[] = ['deadline', 'interval', 'ordrenr', 'type', 'status', 'partner', 'changed', 'created'];
  pastColumns: string[] = ['ordrenr', 'type', 'status', 'partner', 'leveringsdato', 'modified', 'created'];
  caller: string = 'OK.Egis.Portal';

  @Input() serviceaftale: ServiceaftaleDto;

  unsubscribe$ = new Subject<void>();

  constructor(
    private serviceordreService: ServiceordreService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private signalR: SignalrService,
    private ordreService: OrdreService
  ) {}

  ngOnInit(): void {
    this.signalR.serviceordreCreated$.pipe(takeUntil(this.unsubscribe$)).subscribe((serviceordre: ServiceordreDto) => {
      if (serviceordre.serviceaftaleNo == this.serviceaftale.serviceAftaleNo) {
        this.getServiceordrer();
      }
    });

    this.signalR.serviceordreChanged$.pipe(takeUntil(this.unsubscribe$)).subscribe((serviceordre: ServiceordreDto) => {
      if (serviceordre.serviceaftaleNo == this.serviceaftale.serviceAftaleNo) {
        this.getServiceordrer();
      }
    });

    this.getServiceordrer();
  }

  getServiceordrer() {
    this.activePage = 1;
    this.pastPage = 1;
    this.activeServiceordrer = [];
    this.pastServiceordrer = [];
    this.loader.show();
    forkJoin(this.getActiveServiceordrer$, this.getPastServiceordrer$)
      .pipe(
        finalize(() => {
          this.loader.hide();
        })
      )
      .subscribe(
        (response) => {
          this.activePageCount = response[0].pageCount;
          this.pastPageCount = response[1].pageCount;

          this.activeServiceordrer = response[0].serviceordrer;
          this.pastServiceordrer = response[1].serviceordrer;
        },
        (error) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  getActiveServiceordrer$ = defer(() => {
    const request: GetServiceordrerRequest = {
      pageTop: 1,
      pageSkip: this.activePage > 0 ? this.activePage - 1 : 0,
      serviceordreStateDtoIds: [1, 2, 3, 9, 10, 11, 12, 13],
      serviceaftaleNoList: [this.serviceaftale.serviceAftaleNo],
      orderDir: 'DESC',
      orderBy: 'Ajourtid',
      showParked: true,
    };

    return this.serviceordreService.getServiceordrer({ caller: EGIS_SERVICE_CALLER, body: request }).pipe(
      tap((response) => {
        this.activePageCount = response.pageCount;
        this.activeServiceordrer = this.activeServiceordrer.concat(response.serviceordrer);
      }),
      takeUntil(this.unsubscribe$)
    );
  });

  getPastServiceordrer$ = defer(() => {
    const request: GetServiceordrerRequest = {
      pageTop: 1,
      pageSkip: this.pastPage > 0 ? this.pastPage - 1 : 0,
      serviceordreStateDtoIds: [5, 6, 7, 8],
      serviceaftaleNoList: [this.serviceaftale.serviceAftaleNo],
      orderDir: 'DESC',
      orderBy: 'Ajourtid',
    };

    return this.serviceordreService.getServiceordrer({ caller: EGIS_SERVICE_CALLER, body: request }).pipe(
      tap((response) => {
        this.pastPageCount = response.pageCount;
        this.pastServiceordrer = this.pastServiceordrer.concat(response.serviceordrer);
      }),
      takeUntil(this.unsubscribe$)
    );
  });

  moreActive() {
    this.activePage += 1;
    this.loader.show();
    this.getActiveServiceordrer$
      .pipe(
        finalize(() => {
          this.loader.hide();
        })
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
          this.activePage -= 1;
        }
      );
  }

  morePast() {
    this.pastPage += 1;
    this.loader.show();
    this.getPastServiceordrer$
      .pipe(
        finalize(() => {
          this.loader.hide();
        })
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
          this.pastPage -= 1;
        }
      );
  }

  getNotes(serviceordre: ServiceordreDto) {
    return `${serviceordre.description}\n${serviceordre.fastOrdreNote}`;
  }

  handleValidationFailed(event: Event, serviceordre: ServiceordreDto) {
    event.stopPropagation();

    if (serviceordre.serviceordreValidationFailedReason == ServiceordreValidationFailedReasonEnum.CreditCheckFailed) {
      this.ordreService
        .kreditstop$(serviceordre)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          () => {},
          (error) => {
            this.errorHandler.HandleError(error);
          }
        );
    } else {
      this.ordreService
        .handleValidationFailed$(serviceordre)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          () => {},
          (error) => {
            this.errorHandler.HandleError(error);
          }
        );
    }
  }
}
